/** Task type for persona generate-scripts / briefs / suggest jobs. */
export const PERSONA_GENERATE_SCRIPTS_TASK_TYPE = "generate-persona-scripts"

/**
 * Adge "Generate for tenants": one card per tenant, one subtask per source script.
 * Each script job stamps its own one-script count on its subtask, so the card
 * counts subtasks instead of reading that metadata.
 */
export const SCRIPTS_BROADCAST_TASK_TYPE = "scripts-broadcast"

/**
 * Broadcast cards run one job per subtask, so one failed job says nothing
 * about the others: the card stays active until every subtask has finished
 * or failed. Other task types keep failing on the first error.
 */
export function settlesOnEverySubtask(taskType: string): boolean {
  return taskType === SCRIPTS_BROADCAST_TASK_TYPE
}

export type CountBag = { completed: number; total: number }

export type CountNoun = "scripts" | "briefs" | "creatives"

export type CountMetadataSource = {
  subtasks: Array<{ metadata?: Record<string, unknown> }>
}

export type CountProgressSource = {
  subtasks: Array<{
    progress: number
    hasErrors?: boolean
    metadata?: Record<string, unknown>
  }>
}

export type GenerateCountDisplay = {
  hero: CountBag
  heroNoun: CountNoun
  /** Briefs bag when both sides are present. */
  secondary?: CountBag
  bar: CountBag
  minimizedLabel: string
}

export type GenerateCountCopy = {
  totalLabel: string
  secondaryLabel?: string
  ariaValueText: string
}

/** i18next `t` — keys live under `taskProgress.generate` in `locales/en.json`. */
export type CountTranslate = (
  key: string,
  options: Record<string, string | number>,
) => string

function readCountMetadata(
  stages: CountMetadataSource[],
  completedKey: string,
  totalKey: string,
): CountBag | null {
  for (const stage of stages) {
    for (const subtask of stage.subtasks) {
      const completed = subtask.metadata?.[completedKey]
      const total = subtask.metadata?.[totalKey]
      if (typeof completed !== "number" || typeof total !== "number") continue
      if (
        Number.isInteger(completed) &&
        Number.isInteger(total) &&
        completed >= 0 &&
        total > 0 &&
        completed <= total
      ) {
        return { completed, total }
      }
    }
  }
  return null
}

function ofNounLabel(t: CountTranslate, noun: CountNoun, total: number): string {
  if (noun === "scripts") {
    return t("taskProgress.generate.ofScripts", {
      count: total,
      defaultValue_one: "of {{count}} script",
      defaultValue_other: "of {{count}} scripts",
    })
  }
  if (noun === "creatives") {
    return t("taskProgress.generate.ofCreatives", {
      count: total,
      defaultValue_one: "of {{count}} creative",
      defaultValue_other: "of {{count}} creatives",
    })
  }
  return t("taskProgress.generate.ofBriefs", {
    count: total,
    defaultValue_one: "of {{count}} brief",
    defaultValue_other: "of {{count}} briefs",
  })
}

function progressLabel(t: CountTranslate, noun: CountNoun, bag: CountBag): string {
  if (noun === "scripts") {
    return t("taskProgress.generate.scriptsProgress", {
      count: bag.total,
      completed: bag.completed,
      total: bag.total,
      defaultValue_one: "{{completed}} of {{total}} script",
      defaultValue_other: "{{completed}} of {{total}} scripts",
    })
  }
  if (noun === "creatives") {
    return t("taskProgress.generate.creativesProgress", {
      count: bag.total,
      completed: bag.completed,
      total: bag.total,
      defaultValue_one: "{{completed}} of {{total}} creative",
      defaultValue_other: "{{completed}} of {{total}} creatives",
    })
  }
  return t("taskProgress.generate.briefsProgress", {
    count: bag.total,
    completed: bag.completed,
    total: bag.total,
    defaultValue_one: "{{completed}} of {{total}} brief",
    defaultValue_other: "{{completed}} of {{total}} briefs",
  })
}

/**
 * Classic Opportunities hides the footer bar until writing totals exist.
 * Persona generate jobs match that: checklist only during prep, count bar
 * once `readGenerateCountDisplay` finds a bag. Other task types keep a
 * whole-job bar from subtask progress.
 */
export function hideProgressBarUntilGenerateCounts(
  taskType: string,
  hasGenerateCounts: boolean,
): boolean {
  return taskType === PERSONA_GENERATE_SCRIPTS_TASK_TYPE && !hasGenerateCounts
}

/**
 * Persona generate jobs stamp script and/or brief counts on the generate subtask;
 * Pre-Flight stamps creative counts on its scoring subtask.
 */
export function readGenerateCountDisplay(
  stages: CountMetadataSource[],
): GenerateCountDisplay | null {
  const creatives = readCountMetadata(stages, "completedCreatives", "totalCreatives")
  if (creatives) {
    return {
      hero: creatives,
      heroNoun: "creatives",
      bar: creatives,
      minimizedLabel: `${creatives.completed}/${creatives.total}`,
    }
  }

  const scripts = readCountMetadata(stages, "completedScripts", "totalScripts")
  const briefs = readCountMetadata(stages, "completedBriefs", "totalBriefs")
  if (scripts && briefs) {
    return {
      hero: scripts,
      heroNoun: "scripts",
      secondary: briefs,
      bar: {
        completed: scripts.completed + briefs.completed,
        total: scripts.total + briefs.total,
      },
      minimizedLabel: `${scripts.completed}/${scripts.total}`,
    }
  }
  if (briefs) {
    return {
      hero: briefs,
      heroNoun: "briefs",
      bar: briefs,
      minimizedLabel: `${briefs.completed}/${briefs.total}`,
    }
  }
  if (scripts) {
    return {
      hero: scripts,
      heroNoun: "scripts",
      bar: scripts,
      minimizedLabel: `${scripts.completed}/${scripts.total}`,
    }
  }
  return null
}

export function formatGenerateCountCopy(
  display: GenerateCountDisplay,
  t: CountTranslate,
): GenerateCountCopy {
  const totalLabel = ofNounLabel(t, display.heroNoun, display.hero.total)
  const heroProgress = progressLabel(t, display.heroNoun, display.hero)
  if (!display.secondary) {
    return { totalLabel, ariaValueText: heroProgress }
  }
  const secondaryLabel = progressLabel(t, "briefs", display.secondary)
  return {
    totalLabel,
    secondaryLabel,
    ariaValueText: t("taskProgress.generate.ariaBoth", {
      scripts: heroProgress,
      briefs: secondaryLabel,
      defaultValue: "{{scripts}}, {{briefs}}",
    }),
  }
}

/** One script per subtask; done = finished without errors. */
function readSubtaskScriptCounts(stages: CountProgressSource[]): GenerateCountDisplay | null {
  const subtasks = stages.flatMap((stage) => stage.subtasks)
  if (subtasks.length === 0) return null
  const scripts: CountBag = {
    completed: subtasks.filter((subtask) => subtask.progress === 1 && !subtask.hasErrors).length,
    total: subtasks.length,
  }
  return {
    hero: scripts,
    heroNoun: "scripts",
    bar: scripts,
    minimizedLabel: `${scripts.completed}/${scripts.total}`,
  }
}

/** Count display for a task card, by task type. */
export function readTaskCountDisplay(
  taskType: string,
  stages: CountProgressSource[],
): GenerateCountDisplay | null {
  if (taskType === SCRIPTS_BROADCAST_TASK_TYPE) {
    return readSubtaskScriptCounts(stages)
  }
  return readGenerateCountDisplay(stages)
}

/**
 * Count line kept on a finished card. Only scripts broadcasts keep it, so the
 * done card still says how many of the tenant's scripts were written.
 */
export function readTerminalCountSummary(
  taskType: string,
  stages: CountProgressSource[],
  t: CountTranslate,
): string | null {
  if (taskType !== SCRIPTS_BROADCAST_TASK_TYPE) return null
  const display = readSubtaskScriptCounts(stages)
  if (!display) return null
  const written = progressLabel(t, "scripts", display.hero)
  const failed = stages.flatMap((stage) => stage.subtasks).filter((subtask) => subtask.hasErrors).length
  if (failed === 0) return written
  // Final breakdown: how many were written and how many failed.
  return `${written} · ${t("taskProgress.generate.scriptsFailed", {
    count: failed,
    defaultValue_one: "{{count}} failed",
    defaultValue_other: "{{count}} failed",
  })}`
}
